import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Form, Row, Col, notification, Select } from 'antd';
import { toJS } from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import styled from 'styled-components';

import { StyledPitstopModal } from './common/styled';
import { ShopStore, UserStore, AppStore } from 'stores';
import TimePickerDropdown from './common/TimePickerDropdown';

import { initializeShopUsers } from './common/shopUsersUtil';
import { cancelButtonProps } from './common/styled';

export const WEEK_DAYS = [
  { name: 'Sunday', id: 0 },
  { name: 'Monday', id: 1 },
  { name: 'Tuesday', id: 2 },
  { name: 'Wednesday', id: 3 },
  { name: 'Thursday', id: 4 },
  { name: 'Friday', id: 5 },
  { name: 'Saturday', id: 6 },
];

const TIME_ZONE = 'America/Toronto';

const StyledP = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
`;

const AddEmailReportAlertModal = observer(({ id }) => {
  const [form] = Form.useForm();
  const [state, setState] = useState({
    shopUsers: [],
    users: [],
  });

  const handleUsersUpdate = (usersObj) => {
    setState({
      shopUsers: usersObj.shopUsers,
      users: usersObj.users,
    });
  };

  useEffect(() => {
    const { init, shopChangeWatcher } = initializeShopUsers(
      UserStore,
      ShopStore,
      handleUsersUpdate
    );

    init();
    return () => {
      if (shopChangeWatcher) {
        shopChangeWatcher();
      }
    };
  }, []);

  const createDateObject = (time, days) => {
    const timeObj = moment(time, 'h:mma');
    return ShopStore.generateDateObject(moment(), days, timeObj);
  };

  const getDiagnosticNotifications = () => {
    const shopNotifications = toJS(
      ShopStore.shopNotifications.get(ShopStore.currentShop.id)
    );
    return _.chain(shopNotifications)
      .get('data.emailConfiguration')
      .filter((a) => a.email_report_type === 'DiagnosticEmail')
      .value();
  };

  const getUserById = (userId) => {
    const user = state.shopUsers.find((u) => u.userId === userId);
    return { id: user.userId, ...user };
  };

  const createNewNotification = async (
    user,
    diagnosticNotifications,
    dateObject
  ) => {
    await ShopStore.checkAndRemoveEmailBasedNotificationsForUser(
      diagnosticNotifications,
      user
    );
    await ShopStore.sendEmailVehicleHealthReportConfig(
      ShopStore.currentShop.id,
      [user.id],
      dateObject,
      TIME_ZONE
    );
  };

  const updateExistingNotification = async (report, user, dateObject) => {
    await ShopStore.updateEmailVehicleHealthReportConfig(
      report.id,
      ShopStore.currentShop.id,
      [user.id],
      dateObject,
      TIME_ZONE
    );
  };

  const processUserNotification = async (
    userId,
    diagnosticNotifications,
    dateObject
  ) => {
    const user = getUserById(userId);
    const userReports = diagnosticNotifications.filter(
      (a) => a.id_user === userId
    );

    if (_.isEmpty(userReports)) {
      await createNewNotification(user, diagnosticNotifications, dateObject);
    } else {
      await updateExistingNotification(userReports[0], user, dateObject);
    }
  };

  const processFormSubmission = async (values) => {
    const { users, days, time } = values;
    const dateObject = createDateObject(time, days);
    const diagnosticNotifications = getDiagnosticNotifications();

    for (const userId of users) {
      await processUserNotification(
        userId,
        diagnosticNotifications,
        dateObject
      );
    }
  };

  const handleSuccessfulSubmission = async () => {
    notification.success({
      message: 'Weekly Report Scheduled',
      description: 'Continue scheduling reports or close the modal',
    });

    form.resetFields();
    await ShopStore.getShopNotifications(ShopStore.currentShop.id, true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await processFormSubmission(values);
      await handleSuccessfulSubmission();
    } catch (error) {
      if (error.errorFields) {
        AppStore.addError('Form validation error:', error);
      } else {
        AppStore.addError('Failed to save changes. Please try again.');
      }
    }
  };

  return (
    <StyledPitstopModal
      id={id}
      title="Add Weekly Maintenance Report Schedule"
      width={570}
      okText="Add Schedule"
      closable
      isManualClose
      onOk={handleOk}
      onCancel={() => {}}
      cancelButtonProps={cancelButtonProps}
    >
      <StyledP>
        A maintenance report email will be sent automatically on the day of the
        week and time you select.
      </StyledP>

      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <Col xs={12} sm={12}>
            <Form.Item
              name="users"
              label="Select User(s)"
              rules={[{ required: true, message: 'Please select User(s)' }]}
            >
              <Select
                style={{ width: '100%' }}
                mode="multiple"
                allowClear
                options={state.users.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12} sm={12}>
            <Form.Item
              name="days"
              label="Run every"
              rules={[
                { required: true, message: 'Please select a day of the week' },
              ]}
            >
              <Select
                style={{ width: '100%' }}
                allowClear
                options={WEEK_DAYS.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={12} sm={12}>
            <TimePickerDropdown
              name="time"
              label="Run At"
              rules={[
                {
                  required: true,
                  message: 'Please select a time to send the weekly report',
                },
              ]}
            />
          </Col>
        </Row>
      </Form>
    </StyledPitstopModal>
  );
});

AddEmailReportAlertModal.propTypes = {
  id: PropTypes.string,
};

export default AddEmailReportAlertModal;
